import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { UpdateSoftwareDialog } from './components/UpdateSoftwareDialog';
import { RebootDialog } from './components/RebootDialog';
import { UnassignDialog } from './components/UnassignDialog';
import { DeleteDatabaseDialog } from './components/DeleteDatabaseDialog';
import { FactoryResetDialog } from './components/FactoryResetDialog';
import { EnvSwitchDialog } from './components/EnvSwitchDialog';
import { ActionCard } from './components/ActionCard';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDeviceShadow,
  updateDeviceShadow,
} from '../../../../store/devices/actionCreators';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../../store';
import { Device, ShadowConfig } from '../../../../store/devices/deviceTypes';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import { fetchOverviewStats } from '../../../../store/overviewStats/actionCreators';
import { SoftwareVersion } from '../../../../store/software/softwareTypes';
import { AnyOverviewStatsResponse } from '../../../../store/overviewStats/overviewStatsTypes';

type DialogType =
  | 'unassign'
  | 'delete-database'
  | 'update'
  | 'factory-reset'
  | 'reboot'
  | 'env-switch'
  | null;

interface TabComponentProps {
  selectedDevice: Device | null;
  isFacilityAdmin?: boolean;
  isInternalPrivileged?: boolean;
}

const actionCards = [
  {
    id: 'pouch-nick',
    title: 'Pouch nick',
    description: 'Enable or disable the nick in the med pouch',
    hasToggle: true,
    configKey: 'dispenser_enable_nick',
  },
  {
    id: 'unassign',
    title: 'Unassign',
    description: 'This will delete the database and unassign the hub.',
    hasChevron: true,
    dialog: 'unassign' as DialogType,
  },
  {
    id: 'demo-mode',
    title: 'Demo mode',
    description: 'Enable or disable demo mode on the Hub menu',
    hasToggle: true,
    configKey: 'demo_mode_allowed',
  },
  {
    id: 'delete-database',
    title: 'Delete database',
    description: 'This will delete the database but not unassign the Hub.',
    hasChevron: true,
    dialog: 'delete-database' as DialogType,
  },
  {
    id: 'update-software',
    title: 'Update software',
    description: 'Latest version: {version}',
    hasChevron: true,
    dialog: 'update' as DialogType,
  },
  {
    id: 'factory-reset',
    title: 'Factory reset',
    description:
      'This will delete the database, unassign the Hub and download the LTS version.',
    hasChevron: true,
    dialog: 'factory-reset' as DialogType,
  },
  {
    id: 'reboot',
    title: 'Reboot',
    description: 'This will reboot the Hub and all data will be saved.',
    hasChevron: true,
    dialog: 'reboot' as DialogType,
  },
  {
    id: 'env-switch',
    title: 'Switch environment',
    description:
      'Move this hub to a different environment (production / staging / uat) via OTA.',
    hasChevron: true,
    dialog: 'env-switch' as DialogType,
  },
];

const ActionsTab: React.FC<TabComponentProps> = ({
  selectedDevice,
  isFacilityAdmin,
  isInternalPrivileged,
}) => {
  const { connected, shadowConfigs, isLoading, stats } = useSelector<
    RootState,
    {
      connected: boolean;
      shadowConfigs: ShadowConfig[] | null;
      softwareVersions: SoftwareVersion[] | null;
      isLoading: boolean;
      stats: AnyOverviewStatsResponse | null;
    }
  >((state) => {
    const { mqtt, devices, software, overviewStats } = state;
    const { connected } = mqtt;
    const { shadowConfigs, isLoading } = devices;
    const { softwareVersions } = software;
    const { stats } = overviewStats;
    return {
      connected,
      shadowConfigs,
      softwareVersions,
      isLoading,
      stats,
    };
  });

  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState<DialogType>(null);

  const getShadowConfigValue = (configName: string): boolean => {
    if (!shadowConfigs?.length) return false;
    return (
      shadowConfigs.find((config) => config.configName === configName)
        ?.reportedValue ?? false
    );
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchDeviceShadow(Number(id)));
    }
  }, [dispatch, connected, id]);

  useEffect(() => {
    if (!stats) {
      dispatch(fetchOverviewStats());
    }
  }, [dispatch, stats]);

  const handleToggle = (configKey: string) => (checked: boolean) => {
    if (!id) return;
    dispatch(
      updateDeviceShadow(
        Number(id),
        { [configKey]: checked },
        { shouldReload: false },
      ),
    );
  };

  if (isLoading) return <LoadingSpinner />;

  const renderDialog = () => {
    if (!id) return null;
    const deviceId = Number(id);

    switch (openDialog) {
      case 'unassign':
        return (
          <UnassignDialog
            onClose={() => setOpenDialog(null)}
            deviceId={deviceId}
          />
        );
      case 'delete-database':
        return (
          <DeleteDatabaseDialog
            onClose={() => setOpenDialog(null)}
            deviceId={deviceId}
          />
        );
      case 'update':
        return (
          <UpdateSoftwareDialog
            onClose={() => setOpenDialog(null)}
            currentVersion={selectedDevice?.pinned_software_version_id || 0}
            deviceId={deviceId}
            deviceSocId={selectedDevice?.soc_id || 0}
            isFacilityAdmin={isFacilityAdmin}
            isInternalPrivileged={isInternalPrivileged}
          />
        );
      case 'factory-reset':
        return (
          <FactoryResetDialog
            onClose={() => setOpenDialog(null)}
            deviceId={deviceId}
          />
        );
      case 'reboot':
        return (
          <RebootDialog
            onClose={() => setOpenDialog(null)}
            deviceId={deviceId}
          />
        );
      case 'env-switch':
        return (
          <EnvSwitchDialog
            onClose={() => setOpenDialog(null)}
            deviceId={deviceId}
            deviceSocId={selectedDevice?.soc_id || 0}
          />
        );
      default:
        return null;
    }
  };

  const filteredActionCards = actionCards.filter((card) => {
    if (
      isFacilityAdmin &&
      ['delete-database', 'factory-reset', 'pouch-nick', 'demo-mode'].includes(
        card.id,
      )
    ) {
      return false;
    }
    if (card.id === 'env-switch' && !isInternalPrivileged) {
      return false;
    }
    return true;
  });

  return (
    <Box sx={{ mx: 'auto', p: 2, flexWrap: 'wrap' }} display="flex" gap={2}>
      {filteredActionCards.map((card) => (
        <ActionCard
          key={card.id}
          title={card.title}
          description={card.description.replace(
            '{version}',
            stats?.stable_channel_software_version || 'N/A',
          )}
          hasToggle={card.hasToggle}
          hasChevron={card.hasChevron}
          isToggled={
            card.configKey ? getShadowConfigValue(card.configKey) : undefined
          }
          onToggle={card.configKey ? handleToggle(card.configKey) : undefined}
          onClick={card.dialog ? () => setOpenDialog(card.dialog) : undefined}
        />
      ))}
      {renderDialog()}
    </Box>
  );
};

export default ActionsTab;
